// AstriaMexicoShell.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap,
  Users,
  Sparkles,
  Heart,
  MessageCircle,
  Sun,
  Moon,
  UserCircle,
} from "lucide-react";
import { AstriaMexicoThemeProvider, useMexicoColors } from "./AstriaMexicoThemeContext";
import { MX_HEADING_FONT, MX_BODY_FONT, SHELL_COLORS_MEXICO } from "./astriaMexicoTheme";

// ─── Astria Mexico — shared shell (header + sticky tab bar + theme toggle) ──
// Warm, expressive, grounded Mexican Spanish design system — per the
// backend's MX_TONE_MATRIX (helper/astriaMexicoService.js): terracotta
// accent, warm-neutral background, clean-outline buttons, soft-glow hover,
// 14px card radius, 20px padding — mirrors the UK V2 shell's structure with
// Mexico's own palette and copy.

export { MX_HEADING_FONT, MX_BODY_FONT, SHELL_COLORS_MEXICO };
export type { MXPalette } from "./astriaMexicoTheme";
export { useMexicoColors } from "./AstriaMexicoThemeContext";

export type AstriaMexicoTab =
  | "home"
  | "energy-match"
  | "compatibility"
  | "cosmic-message"
  | "relationship"
  | "companion-talk"
  | "daily-flow"
  | "zodiac-personality";

interface TabDef {
  key: AstriaMexicoTab;
  label: string;
  sublabel: string;
  icon: React.ReactNode;
  path: string;
}

const TABS: TabDef[] = [
  {
    key: "home",
    label: "Inicio",
    sublabel: "Empezar",
    icon: <span style={{ fontSize: 14 }}>✦</span>,
    path: "/astria-mexico/home",
  },
  {
    key: "energy-match",
    label: "Energy Match",
    sublabel: "Conexión de energía",
    icon: <Zap className="w-4 h-4" />,
    path: "/astria-mexico/energy-match",
  },
  {
    key: "compatibility",
    label: "Compatibilidad",
    sublabel: "Dos ritmos",
    icon: <Users className="w-4 h-4" />,
    path: "/astria-mexico/compatibility",
  },
  {
    key: "cosmic-message",
    label: "Mensaje Cósmico",
    sublabel: "Tu día en el cielo",
    icon: <Sparkles className="w-4 h-4" />,
    path: "/astria-mexico/cosmic-message",
  },
  {
    key: "relationship",
    label: "Relación",
    sublabel: "Lectura honesta",
    icon: <Heart className="w-4 h-4" />,
    path: "/astria-mexico/relationship",
  },
  {
    key: "companion-talk",
    label: "Companion Talk",
    sublabel: "Conversación",
    icon: <MessageCircle className="w-4 h-4" />,
    path: "/astria-mexico/companion-talk",
  },
  {
    key: "daily-flow",
    label: "Daily Flow",
    sublabel: "Tu día",
    icon: <Sun className="w-4 h-4" />,
    path: "/astria-mexico/daily-flow",
  },
  {
    key: "zodiac-personality",
    label: "Personalidad",
    sublabel: "Tu signo",
    icon: <UserCircle className="w-4 h-4" />,
    path: "/astria-mexico/zodiac-personality",
  },
];

interface AstriaMexicoShellProps {
  active: AstriaMexicoTab;
  children: React.ReactNode;
}

const ThemeToggle: React.FC = () => {
  const { theme, toggleTheme, colors } = useMexicoColors();
  return (
    <button
      onClick={toggleTheme}
      aria-label={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
      className="flex items-center justify-center rounded-full transition-all flex-shrink-0"
      style={{
        width: 34,
        height: 34,
        border: `1px solid ${colors.cardBorder}`,
        background: colors.section,
        color: colors.accent,
      }}
    >
      {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
};

// Shared cap for header/tab-bar/body so the panel fills more of the chat
// column on wide screens instead of floating with large side margins.
const MEXICO_MAX_WIDTH = 960;

const ShellInner: React.FC<AstriaMexicoShellProps> = ({ active, children }) => {
  const navigate = useNavigate();
  const { colors } = useMexicoColors();

  return (
    <div
      className="min-h-screen transition-colors duration-300"
      style={{ background: colors.bg, fontFamily: MX_BODY_FONT }}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          background: colors.headerBg,
          borderBottom: `1px solid ${colors.cardBorder}`,
        }}
      >
        <div
          className="mx-auto flex items-center gap-3 px-5"
          style={{ maxWidth: MEXICO_MAX_WIDTH, paddingTop: 18, paddingBottom: 14 }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              border: `1.5px solid ${colors.accent}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              background: colors.card,
            }}
          >
            <span style={{ fontSize: 15, color: colors.accent }}>✦</span>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: colors.titleColor,
                fontFamily: MX_HEADING_FONT,
                letterSpacing: "0.01em",
              }}
            >
              Astria México
            </div>
            <div style={{ fontSize: 11.5, color: colors.mutedColor }}>
              Cálida, honesta, cercana — la voz mexicana
            </div>
          </div>
          <ThemeToggle />
        </div>

        {/* ─── Tab bar ───────────────────────────────────────────────── */}
        <div style={{ position: "relative" }}>
          <div
            className="mx-auto px-5 pb-3 hide-scrollbar"
            style={{
              maxWidth: MEXICO_MAX_WIDTH,
              display: "flex",
              gap: 8,
              overflowX: "auto",
              scrollbarWidth: "none",
            }}
          >
            {TABS.map((tab) => {
              const isActive = tab.key === active;
              return (
                <button
                  key={tab.key}
                  onClick={() => navigate(tab.path)}
                  className="flex items-center gap-1.5 rounded-full transition-all whitespace-nowrap"
                  style={{
                    paddingInline: 14,
                    paddingBlock: 8,
                    fontSize: 12.5,
                    fontWeight: 600,
                    flexShrink: 0,
                    color: isActive ? colors.white : colors.bodyColor,
                    background: isActive ? colors.accent : colors.section,
                    border: isActive ? "none" : `1px solid ${colors.cardBorder}`,
                  }}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
          {/* Right-edge fade so a clipped last tab reads as "scroll for
              more" rather than a layout bug — the row has no scrollbar
              (hidden above) so this is the only affordance. Reacts to the
              light/dark theme toggle via colors.bg. */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 12,
              width: 28,
              pointerEvents: "none",
              background: `linear-gradient(90deg, ${colors.bg}00 0%, ${colors.bg}E6 100%)`,
            }}
          />
        </div>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <div
        className="mx-auto px-4 sm:px-5"
        style={{ maxWidth: MEXICO_MAX_WIDTH, paddingTop: 24, paddingBottom: 64 }}
      >
        {children}
      </div>
    </div>
  );
};

const AstriaMexicoShell: React.FC<AstriaMexicoShellProps> = (props) => (
  <AstriaMexicoThemeProvider>
    <ShellInner {...props} />
  </AstriaMexicoThemeProvider>
);

export default AstriaMexicoShell;
export { TABS as ASTRIA_MEXICO_TABS };
